import { LineSeriesOption } from 'echarts/charts';
import { HeatmapStatLineType, MIN_STEP_FALLBACK_MS } from '@/core';
import { HeatMapChartData } from '../heat-map-model';

/**
 * Resolve the effective lower/upper bounds for a data point that may span
 * multiple buckets (bucketSpan > 1). Returns null when either boundary bucket
 * is missing.
 */
function getEffectiveBounds(
  bucketIndex: number,
  bucketSpan: number,
  buckets: Array<{ lower: number; upper: number }>
): { lower: number; upper: number } | null {
  const startB = buckets[bucketIndex];
  const endB = buckets[bucketIndex + bucketSpan - 1];
  if (!startB || !endB) return null;
  return { lower: startB.lower, upper: endB.upper };
}

/**
 * Expand multi-span data points into individual sub-bucket entries and
 * normalise to per-bucket counts, matching the iteration pattern in
 * `buildBucketDistribution` (axis.ts). Each returned entry has
 * `bucketSpan: 1` so downstream consumers get correct per-bucket bounds.
 */
function getPerBucketCounts(
  dataPoints: Array<{ value: number; bucketIndex: number; bucketSpan: number }>,
  buckets?: Array<{ lower: number; upper: number }>
): Array<{ bucketCount: number; bucketIndex: number; bucketSpan: number }> {
  const result: Array<{ bucketCount: number; bucketIndex: number; bucketSpan: number }> = [];
  for (const point of dataPoints) {
    const perBucket = point.value / point.bucketSpan;
    for (let i = 0; i < point.bucketSpan; i++) {
      const idx = point.bucketIndex + i;
      const bucket = buckets?.[idx];
      if (bucket && Number.isFinite(bucket.lower) && Number.isFinite(bucket.upper)) {
        result.push({ bucketCount: perBucket, bucketIndex: idx, bucketSpan: 1 });
      }
    }
  }
  return result;
}

export function calculateHistogramAverage(
  dataPoints: Array<{ value: number; bucketIndex: number; bucketSpan: number }>,
  buckets: Array<{ lower: number; upper: number }>
): number | null {
  const perBucket = getPerBucketCounts(dataPoints, buckets);
  let totalWeight = 0;
  let weightedSum = 0;

  for (const point of perBucket) {
    const bounds = getEffectiveBounds(point.bucketIndex, point.bucketSpan, buckets);
    if (!bounds) continue;

    const bucketMidpoint = (bounds.lower + bounds.upper) / 2;
    const weight = point.bucketCount;

    weightedSum += bucketMidpoint * weight;
    totalWeight += weight;
  }

  if (totalWeight === 0) return null;
  return weightedSum / totalWeight;
}

export function calculateHistogramPercentile(
  dataPoints: Array<{ value: number; bucketIndex: number; bucketSpan: number }>,
  buckets: Array<{ lower: number; upper: number }>,
  percentile: number
): number | null {
  const perBucket = getPerBucketCounts(dataPoints, buckets);

  const totalCount = perBucket.reduce((sum, point) => sum + point.bucketCount, 0);
  if (totalCount === 0) return null;

  const targetCount = totalCount * percentile;
  let cumulativeCount = 0;

  for (const point of perBucket) {
    const bounds = getEffectiveBounds(point.bucketIndex, point.bucketSpan, buckets);
    if (!bounds) continue;

    const count = point.bucketCount;
    cumulativeCount += count;

    if (cumulativeCount >= targetCount) {
      const countBeforeBucket = cumulativeCount - count;
      const remainingCount = targetCount - countBeforeBucket;
      const fractionInBucket = count > 0 ? remainingCount / count : 0;

      const bucketWidth = bounds.upper - bounds.lower;
      return bounds.lower + fractionInBucket * bucketWidth;
    }
  }

  const lastPoint = perBucket[perBucket.length - 1];
  const lastBounds = lastPoint ? getEffectiveBounds(lastPoint.bucketIndex, lastPoint.bucketSpan, buckets) : null;
  return lastBounds?.upper ?? null;
}

export function calculateHistogramStdDev(
  dataPoints: Array<{ value: number; bucketIndex: number; bucketSpan: number }>,
  buckets: Array<{ lower: number; upper: number }>,
  precomputedMean?: number
): number | null {
  const mean = precomputedMean ?? calculateHistogramAverage(dataPoints, buckets);
  if (mean === null) return null;

  const perBucket = getPerBucketCounts(dataPoints, buckets);
  let totalWeight = 0;
  let varianceSum = 0;

  for (const point of perBucket) {
    const bounds = getEffectiveBounds(point.bucketIndex, point.bucketSpan, buckets);
    if (!bounds) continue;

    const bucketMidpoint = (bounds.lower + bounds.upper) / 2;
    const weight = point.bucketCount;
    const deviation = bucketMidpoint - mean;

    varianceSum += deviation * deviation * weight;
    totalWeight += weight;
  }

  if (totalWeight === 0) return null;
  return Math.sqrt(varianceSum / totalWeight);
}

const PERCENTILE_MAP: Partial<Record<HeatmapStatLineType, number>> = {
  P1: 0.01,
  P25: 0.25,
  P50: 0.5,
  P75: 0.75,
  P90: 0.9,
  P95: 0.95,
  P99: 0.99,
  P999: 0.999,
  P9999: 0.9999,
  P100: 1.0,
};

export function calculateStatValue(
  lineType: HeatmapStatLineType,
  dataPoints: Array<{ value: number; bucketIndex: number; bucketSpan: number }>,
  buckets: Array<{ lower: number; upper: number }>
): number | null {
  if (lineType === 'Average') {
    return calculateHistogramAverage(dataPoints, buckets);
  }

  if (lineType.startsWith('Stddev')) {
    const avg = calculateHistogramAverage(dataPoints, buckets);
    if (avg === null) return null;
    const stddev = calculateHistogramStdDev(dataPoints, buckets, avg);
    if (stddev === null) return null;

    const sigma = lineType.includes('2') ? 2 : 1;
    const direction = lineType.includes('Upper') ? 1 : -1;
    return avg + direction * sigma * stddev;
  }

  const percentile = PERCENTILE_MAP[lineType];
  if (percentile !== undefined) {
    return calculateHistogramPercentile(dataPoints, buckets, percentile);
  }

  return null;
}

// TODO: use chrono-ui color tokens or map to existing palette
const STAT_LINE_COLORS: Record<HeatmapStatLineType, string> = {
  Average: '#00D9FF',
  Stddev1Upper: '#A0A0FF',
  Stddev1Lower: '#A0A0FF',
  Stddev2Upper: '#8080C0',
  Stddev2Lower: '#8080C0',
  P1: '#00FF00',
  P25: '#00FF44',
  P50: '#00FF88',
  P75: '#0D9488',
  P90: '#0099FF',
  P95: '#3366FF',
  P99: '#6666FF',
  P999: '#9933FF',
  P9999: '#CC00FF',
  P100: '#FF00FF',
};

const STAT_LINE_LABELS: Record<HeatmapStatLineType, string> = {
  Average: 'Avg',
  Stddev1Upper: '+1σ',
  Stddev1Lower: '-1σ',
  Stddev2Upper: '+2σ',
  Stddev2Lower: '-2σ',
  P1: 'P1',
  P25: 'P25',
  P50: 'P50',
  P75: 'P75',
  P90: 'P90',
  P95: 'P95',
  P99: 'P99',
  P999: 'P99.9',
  P9999: 'P99.99',
  P100: 'P100',
};

export function getStatLineColor(lineType: HeatmapStatLineType): string {
  return STAT_LINE_COLORS[lineType] ?? '#FFFFFF';
}

export function getStatLineLabel(lineType: HeatmapStatLineType): string {
  return STAT_LINE_LABELS[lineType] ?? lineType;
}

export function createStatLineSeries(
  heatmapData: HeatMapChartData,
  enabledLines: HeatmapStatLineType[],
  endTimeMs?: number
): LineSeriesOption[] {
  const series: LineSeriesOption[] = [];
  const axisEndMs = endTimeMs ?? heatmapData.timeRange.endMs;

  const stepMs =
    typeof heatmapData.timeRange.stepMs === 'number' && heatmapData.timeRange.stepMs > 0
      ? heatmapData.timeRange.stepMs
      : MIN_STEP_FALLBACK_MS;

  for (const lineType of enabledLines) {
    const seriesData: Array<[number, number | null]> = [];

    for (let i = 0; i < heatmapData.timeSlices.length; i++) {
      const timeSlice = heatmapData.timeSlices[i]!;

      if (i > 0) {
        const prevTs = heatmapData.timeSlices[i - 1]!.timestamp;
        if (timeSlice.timestamp - prevTs > stepMs * 1.5) {
          seriesData.push([prevTs + stepMs, null]);
        }
      }

      const value = calculateStatValue(lineType, timeSlice.dataPoints, heatmapData.buckets);
      if (value !== null) {
        seriesData.push([timeSlice.timestamp, value]);
      }
    }

    if (seriesData.length > 0) {
      const lastPoint = seriesData[seriesData.length - 1];
      if (lastPoint && lastPoint[1] !== null && lastPoint[0] < axisEndMs) {
        const extendTo = Math.min(lastPoint[0] + stepMs, axisEndMs);
        seriesData.push([extendTo, lastPoint[1]]);
      }
    }

    series.push({
      id: lineType,
      name: STAT_LINE_LABELS[lineType],
      type: 'line',
      data: seriesData as Array<[number, number]>,
      step: 'end',
      connectNulls: false,
      symbol: 'circle',
      symbolSize: 0,
      animation: false,
      silent: true,
      emphasis: {
        lineStyle: { width: 2 },
      },
      lineStyle: {
        color: STAT_LINE_COLORS[lineType],
        width: 1,
      },
      itemStyle: {
        color: STAT_LINE_COLORS[lineType],
      },
    });
  }

  return series;
}
